import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
  personnelTags: {}, // { perEntityName: { perEntityID: [tags] } }
  loading: false,
  error: null,
};

// Action Types
const FETCH_PERSONNEL_TAGS_REQUEST = "FETCH_PERSONNEL_TAGS_REQUEST";
const FETCH_PERSONNEL_TAGS_SUCCESS = "FETCH_PERSONNEL_TAGS_SUCCESS";
const FETCH_PERSONNEL_TAGS_FAILURE = "FETCH_PERSONNEL_TAGS_FAILURE";
const CREATE_PERSONNEL_TAG_REQUEST = "CREATE_PERSONNEL_TAG_REQUEST";
const CREATE_PERSONNEL_TAG_SUCCESS = "CREATE_PERSONNEL_TAG_SUCCESS";
const CREATE_PERSONNEL_TAG_FAILURE = "CREATE_PERSONNEL_TAG_FAILURE";
const UPDATE_PERSONNEL_TAG_REQUEST = "UPDATE_PERSONNEL_TAG_REQUEST";
const UPDATE_PERSONNEL_TAG_SUCCESS = "UPDATE_PERSONNEL_TAG_SUCCESS";
const UPDATE_PERSONNEL_TAG_FAILURE = "UPDATE_PERSONNEL_TAG_FAILURE";
const DELETE_PERSONNEL_TAG_REQUEST = "DELETE_PERSONNEL_TAG_REQUEST";
const DELETE_PERSONNEL_TAG_SUCCESS = "DELETE_PERSONNEL_TAG_SUCCESS";
const DELETE_PERSONNEL_TAG_FAILURE = "DELETE_PERSONNEL_TAG_FAILURE";

// Thunks
export const getPersonnelTags =
  (perEntityID, perEntityName, sortField = "perTagID", sortDirection = "asc") =>
  async (dispatch) => {
    const service = getService(serviceNames.personnelTag);
    const query = {
      perEntityID,
      perEntityName,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      isDeleted: false,
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_PERSONNEL_TAGS_REQUEST,
        success: FETCH_PERSONNEL_TAGS_SUCCESS,
        failure: FETCH_PERSONNEL_TAGS_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (result) => ({
        perEntityID,
        perEntityName,
        personnelTags: result,
      }),
      errorMessagePrefix: "Failed to fetch personnel tags",
    });
  };

export const createPersonnelTag = (tagData) => async (dispatch) => {
  const service = getService(serviceNames.personnelTag);
  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: CREATE_PERSONNEL_TAG_REQUEST,
      success: CREATE_PERSONNEL_TAG_SUCCESS,
      failure: CREATE_PERSONNEL_TAG_FAILURE,
    },
    apiCall: async () => {
      const created = await service.create(tagData);
      return service.get(created.perTagID);
    },
    transformResult: (result) => ({
      ...result,
      perEntityID: tagData.perEntityID,
      perEntityName: tagData.perEntityName,
    }),
    successMessage: "Personnel Tag created successfully",
    errorMessagePrefix: "Failed to create personnel tag",
  });
};

export const updatePersonnelTag =
  (personnelTagId, tagData) => async (dispatch) => {
    const service = getService(serviceNames.personnelTag);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_PERSONNEL_TAG_REQUEST,
        success: UPDATE_PERSONNEL_TAG_SUCCESS,
        failure: UPDATE_PERSONNEL_TAG_FAILURE,
      },
      apiCall: async () => {
        await service.patch(personnelTagId, tagData);
        return await service.get(personnelTagId);
      },
      transformResult: (result) => ({
        ...result,
        perEntityID: tagData.perEntityID,
        perEntityName: tagData.perEntityName,
      }),
      successMessage: "Personnel Tag updated successfully",
      errorMessagePrefix: "Failed to update personnel tag",
    });
  };

export const deletePersonnelTag =
  (personnelTagId, userId, perEntityID, perEntityName) => async (dispatch) => {
    const service = getService(serviceNames.personnelTag);
    const patchData = {
      isDeleted: true,
      deletedBy: userId,
      deletedAt: new Date().toISOString(),
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: DELETE_PERSONNEL_TAG_REQUEST,
        success: DELETE_PERSONNEL_TAG_SUCCESS,
        failure: DELETE_PERSONNEL_TAG_FAILURE,
      },
      apiCall: () => service.patch(personnelTagId, patchData),
      transformResult: () => ({
        personnelTagId,
        perEntityID,
        perEntityName,
      }),
      successMessage: "Tag deleted successfully",
      errorMessagePrefix: "Failed to delete tag",
    });
  };

// Reducer
export default function personnelTagsReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_PERSONNEL_TAGS_REQUEST:
    case CREATE_PERSONNEL_TAG_REQUEST:
    case UPDATE_PERSONNEL_TAG_REQUEST:
    case DELETE_PERSONNEL_TAG_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_PERSONNEL_TAGS_SUCCESS: {
      const { perEntityName, perEntityID, personnelTags } = action.payload;
      return {
        ...state,
        loading: false,
        personnelTags: {
          ...state.personnelTags,
          [perEntityName]: {
            ...state.personnelTags[perEntityName],
            [perEntityID]: personnelTags,
          },
        },
      };
    }

    case CREATE_PERSONNEL_TAG_SUCCESS: {
      const { perEntityName, perEntityID } = action.payload;
      const existing = state.personnelTags[perEntityName]?.[perEntityID] ?? [];
      return {
        ...state,
        loading: false,
        personnelTags: {
          ...state.personnelTags,
          [perEntityName]: {
            ...state.personnelTags[perEntityName],
            [perEntityID]: [...existing, action.payload],
          },
        },
      };
    }

    case UPDATE_PERSONNEL_TAG_SUCCESS: {
      const { perEntityName, perEntityID, perTagID } = action.payload;
      const updatedTags =
        state.personnelTags[perEntityName]?.[perEntityID]?.map((tag) =>
          tag.perTagID === perTagID ? action.payload : tag
        ) ?? [];
      return {
        ...state,
        loading: false,
        personnelTags: {
          ...state.personnelTags,
          [perEntityName]: {
            ...state.personnelTags[perEntityName],
            [perEntityID]: updatedTags,
          },
        },
      };
    }

    case DELETE_PERSONNEL_TAG_SUCCESS: {
      const { perEntityName, perEntityID, personnelTagId } = action.payload;
      const filteredTags =
        state.personnelTags[perEntityName]?.[perEntityID]?.filter(
          (tag) => tag.perTagID !== personnelTagId
        ) ?? [];
      return {
        ...state,
        loading: false,
        personnelTags: {
          ...state.personnelTags,
          [perEntityName]: {
            ...state.personnelTags[perEntityName],
            [perEntityID]: filteredTags,
          },
        },
      };
    }

    case FETCH_PERSONNEL_TAGS_FAILURE:
    case CREATE_PERSONNEL_TAG_FAILURE:
    case UPDATE_PERSONNEL_TAG_FAILURE:
    case DELETE_PERSONNEL_TAG_FAILURE:
      return { ...state, loading: false, error: action.payload };

    default:
      return state;
  }
}
